import { ALIGN_TEXT } from 'adapter/in/common/widget/options';
import { populateSerieChartValue } from 'adapter/out/client/widgets/data/mappers/ChartMapper';
import { WidgetType } from 'domain/entity/widget';
import { Color, ColorType, DataAvailabilityKey, PeriodType, WidgetDataAvailability, WidgetDescription } from 'domain/entity/widgetData';
import { ChartAxis, ChartSerieMeta, ChartSeriesType, DateChartValue, DatetimeChartAxisMeta } from 'domain/entity/widgets/ChartData';
import { WidgetChartLimit } from 'domain/entity/widgets/GaugeData';
import ImageData from 'domain/entity/widgets/ImageData';
import RichTextData from 'domain/entity/widgets/RichTextData';
import TextData, { WidgetTextContent, WidgetTextDescription } from 'domain/entity/widgets/TextData';
import { pipe } from 'fp-ts/lib/function';

export type JsonWidgetData = {
    type: WidgetType;
    ['data_availability']: JsonWidgetDataAvailability;
    description: JsonDescription;
};

export type JsonWidgetDataError = {
    message: string;
};

export type JsonColor = {
    type: ColorType;
    ['by_default']: string;
};

export type JsonIcon = {
    key: string;
};

export type JsonDescription = {
    title: { text: string };
    period: {
        type: PeriodType;
        text: string | null;
        from: string | null;
        to: string | null;
    };
};

export type JsonWidgetRawAvailability = {
    available: number;
    expected: number;
    weight: number;
    name: string;
    key: DataAvailabilityKey;
};

export type JsonWidgetDataAvailability = {
    enabled: boolean;
    ['raw_availabilities']: JsonWidgetRawAvailability[];
};

export type ChartLimitSeries = {
    type: ChartSeriesType;
    values: DateChartValue[];
    color: Color;
    axisY: string;
    axisX: string;
    title?: string;
    meta: ChartSerieMeta;
};

export const mapDataAvailability = (dataAvailability: JsonWidgetDataAvailability): WidgetDataAvailability =>
    new WidgetDataAvailability(dataAvailability.raw_availabilities, dataAvailability.enabled);

export const mapDescription = (description: JsonDescription): WidgetDescription =>
    new WidgetDescription(description.title.text, {
        type: PeriodType[description.period.type],
        text: description.period.text,
        from: description.period.from === null ? null : mapDate(description.period.from),
        to: description.period.to === null ? null : mapDate(description.period.to),
    });

export const mapDate = (date: string): Date => new Date(`${date}+00:00`);
export const mapColor = (jsonColor: JsonColor): Color => ({ type: ColorType[jsonColor.type], byDefault: jsonColor.by_default });
export const mapJsonColor = (color: Color): JsonColor => ({ type: ColorType[color.type], by_default: color.byDefault });

// WidgetType.TEXT
export type JsonTextDescription = {
    title: { text: string };
};

export type JsonWidgetTextData = {
    type: WidgetType;
    description: JsonTextDescription;
    data: {
        header: {
            text: string;
        };
        body: {
            text: string;
        };
    };
};
const asWidgetTextDescription = (json: JsonTextDescription): WidgetTextDescription => new WidgetTextDescription(json.title.text);

export const asTextWidget = (json: JsonWidgetTextData): TextData =>
    new TextData(
        new WidgetTextContent(json.data.header.text, ALIGN_TEXT.START),
        new WidgetTextContent(json.data.body.text, ALIGN_TEXT.START),
        asWidgetTextDescription(json.description)
    );

// WidgetType.RICH_TEXT
export type JsonWidgetRichTextData = {
    type: WidgetType;
    ['rich_text']: string;
};

export const asTextBlockWidget = (json: JsonWidgetRichTextData): RichTextData => {
    const decoded = new TextDecoder().decode(
        Uint8Array.from(atob(json.rich_text), c => c.charCodeAt(0))
    );
    return new RichTextData(decoded);
}

// WidgetType.IMAGE
export type JsonWidgetImageData = {
    type: WidgetType;
    description: JsonTextDescription;
    data: {
        url: {
            text: string;
        };
    };
};
const asWidgetImageDescription = (json: JsonTextDescription): WidgetTextDescription => new WidgetTextDescription(json.title.text);

export const asImageWidget = (json: JsonWidgetImageData): ImageData =>
    new ImageData(json.data.url.text, asWidgetImageDescription(json.description));

// Limit Chart / Gauge Checkpoints
export type JsonChartLimit = {
    color: JsonColor;
    value: number;
    name?: string;
};

export const asWidgetChartLimit = (jsons: JsonChartLimit[]): WidgetChartLimit[] =>
    jsons.map((json) => new WidgetChartLimit(mapColor(json.color), json.value, json.name));

export const generateChartLimitSeries = (
    chartLimits: WidgetChartLimit[],
    axisXSelected: string,
    axesX: ChartAxis[],
    axisY: string
): ChartLimitSeries[] =>
    chartLimits.map((limit) => ({
        type: ChartSeriesType.CHART_LIMIT,
        values: pipe(
            axesX.find((axis) => axis.key === axisXSelected)?.meta as DatetimeChartAxisMeta,
            (meta) => populateSerieChartValue(meta.from, meta.to, {}, meta.resolution, limit.value),
            (values) => values
        ),
        color: limit.color,
        axisY,
        axisX: axesX[0].key,
        title: limit.name,
        meta: {},
    }));
